/* The composition root of the client: everything the tree needs, wired once.
 *
 * Exported separately from `main.tsx` so tests can mount the same providers
 * around a single screen without booting the whole app.
 */

import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ApiError } from '../shared/api/client.js';
import { recordRefusal } from '../shared/api/refusals.js';
import { LocaleProvider } from './locale.js';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    /* One place that notices a write refused for being over the plan —
     * STORY-045. Every mutation in the product passes through here, including
     * the ones added next month, which is the point: teaching each form to
     * recognise the code works right up until somebody forgets, and forgetting
     * is silent. */
    mutationCache: new MutationCache({
      onError: (error) => {
        if (error instanceof ApiError && error.code === 'entitlement_exceeded') {
          recordRefusal();
        }
      },
    }),
    defaultOptions: {
      queries: {
        /* One retry, not three. These screens are a person waiting on a form;
         * three silent retries turn a dead server into a fifteen-second stare
         * at a spinner before anything is said. */
        retry: 1,
        refetchOnWindowFocus: false,
      },
      /* A mutation is a deliberate act. Retrying one automatically is how a
       * single click becomes two accounts. */
      mutations: { retry: false },
    },
  });
}

export interface ProvidersProps {
  readonly children: ReactNode;
  readonly queryClient?: QueryClient;
}

export function Providers({ children, queryClient }: ProvidersProps) {
  return (
    <QueryClientProvider client={queryClient ?? createQueryClient()}>
      {/* `LocaleProvider` renders `I18nProvider` with the resolved language.
          Rendering `I18nProvider` directly here is what left the browser
          winning on every screen for everyone — STORY-042. */}
      <LocaleProvider>{children}</LocaleProvider>
    </QueryClientProvider>
  );
}
